import {
  VTextField,
  VTextarea,
  VAutocomplete,
  VSelect,
} from 'vuetify/components';

import DynamicJsonField from '@/components/form/DynamicJsonField.vue'
import FileUploader from '@/components/form/FileUploader.vue';
import DatePickerInput from '@/components/form/DatePickerInput.vue';
import TimePickerInput from '@/components/form/TimePickerInput.vue';
import ActivityDetail from '@/components/modules/activities/Detail.vue'

import type { FormField } from '@/types/form';

function to24hString(time12h: string, period: 'AM' | 'PM'): string {
  let [h, m] = time12h.split(':').map(Number);
  if (period === 'PM' && h < 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
}

function formatTimeValue(val: string): string {
  if (!val) return '';
  // Soporta "12:00 AM", "1:00 PM", "18:30", etc.
  const match = val.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!match) return val; // Si no matchea, regresa tal cual

  let hour = parseInt(match[1], 10);
  const minute = match[2];
  const ampm = match[3];

  if (ampm) {
    if (ampm.toUpperCase() === 'PM' && hour < 12) hour += 12;
    if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
  }

  // Asegura que hour sea string con padStart para 2 dígitos
  return `${hour.toString().padStart(2, '0')}:${minute}`;
}

export function from24hTo12h(time: string): string {
  if (!time) return '';
  const [hh, mm] = time.split(':');
  let hour = parseInt(hh, 10);
  const min = mm;
  const period = hour >= 12 ? 'PM' : 'AM';
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${String(hour).padStart(2, '0')}:${min} ${period}`;
}

export function parseTimeRange(item: any): [string, string] | null {
  if (!item) return null;
  // Espera que vengan las props start_time y end_time (ambas en 24h)
  const start = item.start_time || item.only_time;
  const end = item.end_time;
  if (start && end) {
    return [from24hTo12h(start), from24hTo12h(end)];
  }
  // Si solo es horario único (no rango)
  if (start) return [from24hTo12h(start), ''];
  return null;
}

const idFieldMappings: Record<string, string> = {
  user_id: 'user',
  opportunity_id: 'opportunity'
};

export const CREATE_FORM: FormField[] = [  
  
  { value: 'only_date', label: 'Fecha', component: DatePickerInput, required: true, col: 6, range: false },
  { value: 'only_time', label: 'Horario', component: TimePickerInput, required: true, col: 6, range: false,
    transform: (val: any) => {
      if (!val) return {};
      if (Array.isArray(val)) {
        // Rango de horas
        return {
          start_time: formatTimeValue(val[0]),
          end_time: formatTimeValue(val[1])
        };
      }
      return { only_time: formatTimeValue(val) };
    }
  },
  {
    value: 'date',
    label: 'Fecha y hora',
    transform: (val, form) => {
      const date = form.only_date || '';
      let time = form.only_time || '';
      if (!date || !time) return null;
  
      // Si tiene AM/PM, conviértelo a 24 horas
      if (/(AM|PM)$/i.test(time)) {
        const [hourMin, ampm] = time.split(' ');
        let [hour, min] = hourMin.split(':').map(Number);
        if (/PM/i.test(ampm) && hour < 12) hour += 12;
        if (/AM/i.test(ampm) && hour === 12) hour = 0;
        time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      }
  
      // Agrégale los segundos
      time = `${time}:00`;
  
      return { date: `${date} ${time}` };
    }
  },

  
  { value: 'subject', 
    label: 'Cliente/Lead', 
    component: VAutocomplete, 
    searchValue: 'name',
    required: true, 
    col: 6, 
    mdcol:12, 
    fetchOptions: 'searchClientsAndLeads', 
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.legal_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject_type: value?.type,
    })
  },
  {
    value: 'contact',
    label: 'Contacto',
    component: VAutocomplete,
    searchValue: 'name',
    required: false,
    col: 6,
    fetchOptions: 'searchContacts',
    itemText: (item: any) => {
      // Siempre chequea si el item existe primero para evitar errores
      if (!item) return '';
      const name = item?.name || '';
      const last = item?.last || '';
      // El paréntesis debe cubrir solo lo que corresponde
      return `${name} ${last}`;
    },
    itemValue: (item: any) => item,
    dependsOn: 'subject',
    dependsBehavior: 'disabled',
    dependsCondition: { value: null, condition: 'diferent' },
    fetchParams: (form: any) => ({
      'filter[client_id]': form?.subject.id
    }),
    transform: (value: any) => ({
      contact_id: value?.id,
    })
  },
  {
    value: 'opportunity',
    label: 'Oportunidad',
    component: VAutocomplete,
    required: false,
    col: 6,
    fetchOptions: 'searchOpportunity',
    searchValue: 'opportunity_name',
    itemText: 'opportunity_name',
    itemValue: (item: any) => item,
    dependsOn: 'subject',
    dependsBehavior: 'disabled',
    dependsCondition: { value: null, condition: 'diferent' },
    fetchParams: (form: any) => ({
      'filter[subject_id]': form?.subject.id || null,
      'filter[subject]': form?.subject?.type
    }),
    transform: (value: any) => ({
      opportunity_id: value?.id,
    })
  },
  { value: 'user_id', 
    permission_state:'hidden',
    permission:'activities.assign',
    label: 'Vendedor', 
    component: VAutocomplete, 
    searchValue: 'name', 
    required: false, 
    col: 6, 
    fetchOptions: 'searchUsers', 
    itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '', 
    itemValue: 'id',
    fetchParams: () => ({
      'filter[is_active]': 1
    }),
  },
  { value: 'type', 
    label: 'Actividad', 
    component: VSelect, 
    searchValue: 'name', 
    required: false, 
    col: 6, 
    fetchOptions: 'activityTypesFromStore', 
    itemText: 'name', 
    itemValue: 'name'
  },
  {
    value: 'description',
    label: 'Descripción',
    component: VTextarea,
  },
  /*{
    value: 'additional_data',
    label: 'Documentos',
    isJsonArray: true,
    plainJson: false,
    component: DynamicJsonField,
    col: 12,
    children: [
      {
        value: 'label',
        label: 'Etiqueta',
        component: VTextField,
        col: 6,
        required: false,
      },
      {
        value: 'file',
        label: 'Archivo',
        component: FileUploader,
        module: 'activities', // importante
        col: 6,
        required: false,
      }
    ],
  },*/
];

export const EDIT_FORM: FormField[] = [...CREATE_FORM.map(field => {
  const relatedKey = idFieldMappings[field.value];
  if (relatedKey) {
    return {
      ...field,
      parse: (_: any, item: any) => item[relatedKey] || null,
      transform: (val: any) => ({
        [field.value]: val?.id ?? val
      })
    };
  }
  if (field.value === 'only_time') {
    return {
      ...field,
      parse: (_: any, item: any) => {
        // Rango
        if (item.start_time && item.end_time) {
          return [from24hTo12h(item.start_time), from24hTo12h(item.end_time)];
        }
        // Simple
        if (item.only_time) return from24hTo12h(item.only_time);
        return null;
      },
      // Mantén tu transform igual
      transform: field.transform,
    };
  }
  return field;
})];

export const HEADERS = [
  { title: '', value: 'eye_detail', format: 'text', sortable: false },
  { title: 'ID', value: 'id', format: 'text', sortable: true },
  { title: 'Cliente/Lead', value: 'subject', format: 'text', sortable: false },
  { title: 'Contacto', value: 'contact', format: 'text', sortable: false },
  { title: 'Tipo', value: 'type', format: 'text', sortable: true },
  { title: 'Fecha', value: 'only_date', format: 'text', sortable: true },
  { title: 'Hora', value: 'only_time', format: 'text', sortable: true },
  { title: 'Status', value: 'status.name', format: 'text', sortable: false },
  { title: 'Responsable', value: 'user', format: 'text', sortable: false },
  { title: 'Creación', value: 'created_at', format: 'text', sortable: true },

];

export const DEFAULT_HEADER_ORDER = [
  //'eye_detail',
  'id',
  'subject',
  'contact',
  'type',
  'only_date',
  'only_time',
  'status.name',
  'user',
  'created_at',
];

export const DETAIL = {
  expand_table: false,
  eye_detail: true,
  component: ActivityDetail,
};

export const FILTERS = [
  { value: 'id', label: 'ID', component: VTextField },
  { value: 'type', label: 'Actividad', component: VAutocomplete, col: 6, fetchOptions: 'searchActivityTypes', itemText: 'name', itemValue: 'id', searchValue: 'name',},
  { value: 'subject_id', 
    label: 'Cliente/Lead',
    component: VAutocomplete, 
    searchValue: 'name',
    fetchOptions: 'searchClientsAndLeads', 
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.legal_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject: value?.type
    })
  },
  {
    value: 'contact',
    label: 'Contacto',
    component: VAutocomplete,
    fetchOptions: 'searchContacts',
    searchValue: 'name',
    itemText: (item: any) => {
      // Siempre chequea si el item existe primero para evitar errores
      if (!item) return '';
      const name = item?.name || '';
      const last = item?.last || '';
      const commercial = item?.client?.attributes?.commercial_name || '';
      const legal = item?.client?.attributes?.legal_name || '';
      // El paréntesis debe cubrir solo lo que corresponde
      return `${name} ${last} (${commercial || legal})`;
    },
    itemValue: (item: any) => item,
    fetchParams: () => ({
      'include':'client'
    }),
    transform: (values: any) => ({
      contact_id: Array.isArray(values)
      ?values.map(v=>v.id??v).join(',')
      :values?.id ?? values
    }),
    multiple:true
  },
  { value: 'user_id', 
    label: 'Vendedor', 
    component: VAutocomplete, 
    fetchOptions: 'searchUsers', 
    searchValue: 'name',
    itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '',
    itemValue: (item: any) => item,
    transform: (values: any) => ({
      user_id: Array.isArray(values)
      ?values.map(v=>v.id??v).join(',')
      :values?.id ?? values
    }),
    multiple:true
  },
  { value: 'description', label: 'Descripción', component: VTextField },
];

export const CALENDAR_CONFIG = {
  dateField: 'date',
  filterField: 'date_between',
  labelField: 'type',
  colorField: 'status.color', // opcional
};

export const EVIDENCE_FIELDS = [
  {
    value: 'result',
    label: 'Comentarios',
    component: VTextarea,
    required: true,
    col: 12,
  },
  {
    value: 'documents',
    label: 'Evidencias',
    isJsonArray: true,
    plainJson: false,
    component: DynamicJsonField,
    col: 12,
    children: [
      {
        value: 'label',
        label: 'Etiqueta',
        component: VTextField,
        col: 6,
      },
      {
        value: 'file',
        label: 'Archivo',
        component: FileUploader,
        module: 'activities',
        col: 6,
        required: false,
      },
    ],
  },
];

export const ROW_ACTIONS = [
  { label: 'Editar', onClick: '__edit', permission:'activities.update' },
  { label: 'Eliminar', onClick: '__delete', permission:'activities.delete' },
];
